import Badge from "@/components/shared/Badge/Badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import type { NotazzNfseEnsureResponse } from "@/services/sells.service";
import { SellsService } from "@/services/sells.service";
import { formatInvoiceEmitError } from "@/utils/formatInvoiceEmitError";
import { getIntegrationWithIcon } from "@/utils/integrations";
import { useMutation } from "@tanstack/react-query";
import { AnimatePresence, motion } from "framer-motion";
import { AlertCircle, FileOutput, Loader2 } from "lucide-react";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

type Provider = "notazz" | "enotas";
type ModalPhase =
  | "confirm"
  | "choose"
  | "form"
  | "processing"
  | "success"
  | "error";

const DEFAULT_RETURN_CFOP: Record<Provider, string> = {
  notazz: "5101",
  enotas: "1202",
};

function normalizeCfopInput(value: string): string {
  return value.replace(/\D/g, "").slice(0, 4);
}

interface EmitReturnNoteFiscalButtonProps {
  orderUuid: string;
  orderStatus: string;
  canEmitNotazzReturn: boolean;
  canEmitEnotasReturn: boolean;
  onSuccess?: () => void;
}

export default function EmitReturnNoteFiscalButton({
  orderUuid,
  orderStatus,
  canEmitNotazzReturn,
  canEmitEnotasReturn,
  onSuccess,
}: EmitReturnNoteFiscalButtonProps) {
  const { t } = useTranslation(["sales", "common"]);
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState<ModalPhase>("confirm");
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(
    null,
  );
  const [cfop, setCfop] = useState(DEFAULT_RETURN_CFOP.notazz);
  const [localError, setLocalError] = useState("");
  const [errorDetails, setErrorDetails] = useState("");
  const isOpenRef = useRef(false);

  const emitErrorMessages = {
    timeout: t("sales:noteFiscalTimeout"),
    fallback: t("sales:errorEmittingReturnNote"),
  };

  const applyResult = (data: NotazzNfseEnsureResponse) => {
    if (data.success) {
      onSuccess?.();
      toast({
        title: t("sales:returnNoteEmitted"),
        description: data.message,
        variant: "success",
      });
      if (!isOpenRef.current) return;
      setPhase("success");
      return;
    }
    const details = data.message ?? t("sales:errorEmittingReturnNote");
    if (!isOpenRef.current) {
      toast({
        title: t("sales:errorEmittingReturnNote"),
        description: details,
        variant: "destructive",
      });
      return;
    }
    setPhase("error");
    setErrorDetails(details);
  };

  const applyError = (error: unknown) => {
    const errorData = (error as { response?: { data?: unknown } })?.response
      ?.data as Record<string, unknown> | undefined;
    const err =
      errorData?.error && typeof errorData.error === "object"
        ? (errorData.error as { code?: string })
        : null;
    const details =
      err?.code === "INVALID_CFOP" && typeof errorData?.message === "string"
        ? errorData.message
        : formatInvoiceEmitError(error, emitErrorMessages);

    if (!isOpenRef.current) {
      toast({
        title: t("sales:errorEmittingReturnNote"),
        description: details,
        variant: "destructive",
      });
      return;
    }
    setPhase("error");
    setErrorDetails(details);
  };

  const notazzMutation = useMutation({
    mutationFn: (payload: { cfop: string }) =>
      SellsService.postNotazzNfeReturnSync(orderUuid, payload),
    onSuccess: applyResult,
    onError: applyError,
  });

  const enotasMutation = useMutation({
    mutationFn: (payload: { cfop: string }) =>
      SellsService.postEnotasNfeReturnSync(orderUuid, payload),
    onSuccess: applyResult,
    onError: applyError,
  });

  const beginCfopFlow = (provider: Provider) => {
    setSelectedProvider(provider);
    setCfop(DEFAULT_RETURN_CFOP[provider]);
    setLocalError("");
    setErrorDetails("");
    setPhase("form");
  };

  const handleContinue = () => {
    if (canEmitNotazzReturn && canEmitEnotasReturn) {
      setPhase("choose");
      return;
    }
    if (canEmitNotazzReturn) {
      beginCfopFlow("notazz");
      return;
    }
    if (canEmitEnotasReturn) {
      beginCfopFlow("enotas");
    }
  };

  const handleConfirmEmit = () => {
    const digits = normalizeCfopInput(cfop);
    if (digits.length !== 4) {
      setLocalError(t("sales:returnNoteCfopInvalid"));
      return;
    }
    setLocalError("");
    setPhase("processing");
    if (selectedProvider === "notazz") {
      notazzMutation.mutate({ cfop: digits });
    } else if (selectedProvider === "enotas") {
      enotasMutation.mutate({ cfop: digits });
    }
  };

  const resetState = () => {
    setPhase("confirm");
    setSelectedProvider(null);
    setCfop(DEFAULT_RETURN_CFOP.notazz);
    setLocalError("");
    setErrorDetails("");
    notazzMutation.reset();
    enotasMutation.reset();
  };

  const handleOpen = () => {
    isOpenRef.current = true;
    resetState();
    setOpen(true);
  };

  const handleOpenChange = (nextOpen: boolean) => {
    isOpenRef.current = nextOpen;
    setOpen(nextOpen);
    if (!nextOpen && phase !== "processing") {
      resetState();
    }
  };

  const handleRetry = () => {
    setErrorDetails("");
    setPhase("form");
  };

  if (!canEmitNotazzReturn && !canEmitEnotasReturn) {
    return null;
  }

  if (orderStatus !== "paid") {
    return null;
  }

  const defaultCfopLabel =
    selectedProvider != null
      ? DEFAULT_RETURN_CFOP[selectedProvider]
      : DEFAULT_RETURN_CFOP.notazz;

  const titleByPhase: Record<ModalPhase, string> = {
    confirm: t("sales:confirmReturnNoteTitle"),
    choose: t("sales:chooseReturnNoteProvider"),
    form: t("sales:returnNoteCfopStepTitle"),
    processing: t("sales:processingReturnNote"),
    success: t("sales:returnNoteEmitted"),
    error: t("sales:errorEmittingReturnNote"),
  };

  return (
    <>
      <Button
        variant="secondary"
        className="w-full lg:w-auto"
        onClick={handleOpen}
      >
        <FileOutput size={16} />
        {t("sales:emitReturnNoteFiscal")}
      </Button>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-hidden p-0">
          <div className="flex flex-col max-h-[90vh]">
            <DialogHeader className="p-3 sm:p-6 pb-2 sm:pb-4 border-b border-colorBorder shrink-0">
              <div className="flex items-center justify-between pr-6 sm:pr-8 gap-2">
                <DialogTitle className="text-base sm:text-xl font-semibold truncate">
                  {titleByPhase[phase]}
                </DialogTitle>
                {phase === "success" && (
                  <Badge
                    status="paid"
                    text={t("sales:returnNoteEmitted")}
                  />
                )}
              </div>
            </DialogHeader>

            <div className="overflow-y-auto flex-1 p-4 sm:p-6 space-y-4">
              <AnimatePresence mode="wait">
                {phase === "confirm" ? (
                  <motion.div
                    key="confirm"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="bg-zinc-900/50 rounded-lg p-4 border border-colorBorder"
                  >
                    <p className="text-sm text-muted-foreground">
                      {t("sales:confirmReturnNoteDescription")}
                    </p>
                  </motion.div>
                ) : phase === "choose" ? (
                  <motion.div
                    key="choose"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="space-y-3"
                  >
                    <p className="text-sm text-muted-foreground">
                      {t("sales:chooseReturnNoteProviderDescription")}
                    </p>
                    {canEmitNotazzReturn && (
                      <button
                        type="button"
                        onClick={() => beginCfopFlow("notazz")}
                        className={cn(
                          "w-full text-left bg-zinc-900/50 rounded-lg p-3 sm:p-4 border border-colorBorder",
                          "hover:border-primary/40 transition-colors",
                        )}
                      >
                        {getIntegrationWithIcon("notazz") || (
                          <span className="text-colorText text-sm font-medium">
                            {t("sales:processWithNotazz")}
                          </span>
                        )}
                      </button>
                    )}
                    {canEmitEnotasReturn && (
                      <button
                        type="button"
                        onClick={() => beginCfopFlow("enotas")}
                        className={cn(
                          "w-full text-left bg-zinc-900/50 rounded-lg p-3 sm:p-4 border border-colorBorder",
                          "hover:border-primary/40 transition-colors",
                        )}
                      >
                        {getIntegrationWithIcon("enotas") || (
                          <span className="text-colorText text-sm font-medium">
                            {t("sales:processWithEnotas")}
                          </span>
                        )}
                      </button>
                    )}
                  </motion.div>
                ) : phase === "form" ? (
                  <motion.div
                    key="form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="space-y-4"
                  >
                    <p className="text-sm text-muted-foreground">
                      {t("sales:returnNoteCfopHint", {
                        defaultCfop: defaultCfopLabel,
                      })}
                    </p>
                    <div className="space-y-2">
                      <Label htmlFor="return-note-cfop">
                        {t("sales:notazzReturnCfopLabel")}
                      </Label>
                      <Input
                        id="return-note-cfop"
                        inputMode="numeric"
                        autoComplete="off"
                        maxLength={8}
                        placeholder={t("sales:notazzReturnCfopPlaceholder")}
                        value={cfop}
                        onChange={(e) => {
                          setCfop(normalizeCfopInput(e.target.value));
                          if (localError) setLocalError("");
                        }}
                        aria-invalid={!!localError}
                      />
                      {localError ? (
                        <p className="text-sm text-red-400">{localError}</p>
                      ) : null}
                    </div>
                  </motion.div>
                ) : phase === "processing" ? (
                  <motion.div
                    key="processing"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="bg-zinc-900/50 rounded-lg p-4 border border-colorBorder"
                  >
                    <p className="text-sm text-colorText">
                      {t("sales:emittingReturnNote")}
                    </p>
                  </motion.div>
                ) : phase === "success" ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="bg-zinc-900/50 rounded-lg p-4 border border-colorBorder"
                  >
                    <p className="text-sm text-muted-foreground">
                      {t("sales:returnNoteEmittedDescription")}
                    </p>
                  </motion.div>
                ) : (
                  <motion.div
                    key="error"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="rounded-lg border border-red-500/30 bg-red-500/5 p-4"
                  >
                    <div className="flex items-start gap-2">
                      <AlertCircle
                        className="text-red-400 shrink-0 mt-0.5"
                        size={16}
                      />
                      <div>
                        <p className="text-sm font-medium text-colorText mb-1">
                          {t("sales:errorDetails")}
                        </p>
                        <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                          {errorDetails}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="p-3 sm:p-6 pt-3 sm:pt-4 border-t border-colorBorder bg-background shrink-0">
              {phase === "confirm" && (
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    variant="outline"
                    className="flex-1 h-9 sm:h-10 text-xs sm:text-sm"
                    onClick={() => handleOpenChange(false)}
                  >
                    {t("sales:cancel")}
                  </Button>
                  <Button
                    className="flex-1 h-9 sm:h-10 text-xs sm:text-sm"
                    onClick={handleContinue}
                  >
                    {t("sales:confirmReturnNoteConfirm")}
                  </Button>
                </div>
              )}
              {phase === "choose" && (
                <Button
                  variant="outline"
                  className="w-full h-9 sm:h-10 text-xs sm:text-sm"
                  onClick={() => handleOpenChange(false)}
                >
                  {t("common:close")}
                </Button>
              )}
              {phase === "form" && (
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    variant="outline"
                    className="flex-1 h-9 sm:h-10 text-xs sm:text-sm"
                    onClick={() => handleOpenChange(false)}
                  >
                    {t("sales:cancel")}
                  </Button>
                  <Button
                    className="flex-1 h-9 sm:h-10 text-xs sm:text-sm"
                    onClick={handleConfirmEmit}
                  >
                    {t("sales:returnNoteConfirmSend")}
                  </Button>
                </div>
              )}
              {phase === "processing" && (
                <Button
                  disabled
                  className="w-full h-9 sm:h-10 text-xs sm:text-sm"
                >
                  <Loader2 className="animate-spin mr-2 h-4 w-4" />
                  {t("common:processing")}
                </Button>
              )}
              {phase === "success" && (
                <Button
                  variant="outline"
                  className="w-full h-9 sm:h-10 text-xs sm:text-sm"
                  onClick={() => handleOpenChange(false)}
                >
                  {t("common:close")}
                </Button>
              )}
              {phase === "error" && (
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button
                    className="flex-1 h-9 sm:h-10 text-xs sm:text-sm"
                    onClick={handleRetry}
                  >
                    {t("sales:returnNoteTryAgain")}
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1 h-9 sm:h-10 text-xs sm:text-sm"
                    onClick={() => handleOpenChange(false)}
                  >
                    {t("common:close")}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
